import React from 'react';

import { ICurrencyFormat } from 'utils/types';
import { SpendTypeEnum } from 'components/Classification';
import { DimensionList } from 'components/SpendAllocation/DimensionList';
import { DimensionControls } from 'components/SpendAllocation/DimensionControls';
import { AllocationList } from 'components/SpendAllocation/AllocationList';
import { MiddleContent } from 'components/SpendAllocation/MiddleContent';
import { IOption, colors } from '@alixpartners/ui-components';
import {
  IDimensionSelection,
  IDimensionGroup,
  IAllocatedDimensionGroup,
  IAllocatedItem,
  IApplicationItem,
  IAllocation,
  DimensionTypeEnum,
} from 'components/SpendAllocation/types';
import {
  infoMessage,
  dimensionOptions,
  isAllocationItemValid,
  contributionField,
} from 'components/SpendAllocation/utils';
import {
  LeftSection,
  MainWrapper,
  SideContainer,
  MiddleContainer,
  ScrollWrapper,
  Overlay,
  OverlayContainer,
} from '../styles';

interface IProps {
  permissions: string[];
  format: ICurrencyFormat;
  spendType: SpendTypeEnum;
  dimensions: IDimensionGroup[];
  allocations: IAllocatedItem[];
  applications: IApplicationItem[];
  dimension: IOption;
  onReuseAllocation(): void;
  onDimensionChange(dimension: IOption): void;
  onAllocationChange(allocation: IAllocatedItem): void;
  selectedYear: number;
  isDimensionsLoading?: boolean;
}

interface IState {
  selectedItem: IDimensionSelection | null;
}

export class ApplicationAllocation extends React.Component<IProps, IState> {
  state = {
    selectedItem: null as IState['selectedItem'],
  };

  componentDidUpdate(prevProps: IProps) {
    const { dimension, selectedYear } = this.props;

    if (
      dimension.value !== prevProps.dimension.value ||
      selectedYear !== prevProps.selectedYear
    ) {
      this.setState({ selectedItem: null });
    }
  }

  handleItemSelect = (selection: IDimensionSelection | null) => {
    this.setState({ selectedItem: selection });
  };

  getSelectedParentName = () => {
    const { selectedItem } = this.state;

    if (!selectedItem || !selectedItem.rootId) {
      return null;
    }

    const { dimensions } = this.props;

    const parentItem = dimensions.find(
      (item) => item.id === selectedItem.rootId
    );

    return parentItem?.name;
  };

  getSelectedDimensionItem = () => {
    const { selectedItem } = this.state;

    if (!selectedItem) {
      return null;
    }
    const { dimensions } = this.props;

    if (selectedItem.rootId) {
      const parent = dimensions.find((item) => item.id === selectedItem.rootId);

      return parent?.children?.find((item) => item.id === selectedItem.id);
    }

    return dimensions.find((item) => item.id === selectedItem.id);
  };

  getSelectedAllocation = () => {
    const { selectedItem } = this.state;
    const { allocations } = this.props;

    if (!selectedItem || !allocations || allocations.length === 0) {
      return null;
    }

    const selectedAllocation = allocations.find(
      (item) => item.dimensionId === selectedItem.id
    );

    return selectedAllocation;
  };

  getAllocatedDimensionList = () => {
    const { dimensions, allocations } = this.props;

    const dimensionsWithAllocations: IAllocatedDimensionGroup[] = JSON.parse(
      JSON.stringify(dimensions)
    );

    allocations.forEach((allocation) => {
      if (allocation.rootId) {
        const parent = dimensionsWithAllocations.find(
          (item) => item.id === allocation.rootId
        );

        if (parent && parent.children) {
          const child = parent.children.find(
            (item) => item.id === allocation.dimensionId
          );

          if (child) {
            child.isAllocated = true;
            child.isInvalid = !isAllocationItemValid(allocation.items);
          }
        }
      } else {
        const parent = dimensionsWithAllocations.find(
          (item) => item.id === allocation.dimensionId
        );

        if (parent) {
          parent.isAllocated = true;
          parent.isInvalid = !isAllocationItemValid(allocation.items);
        }
      }
    });

    return dimensionsWithAllocations;
  };

  isGroupSelectionDisabled = () => {
    const { dimension } = this.props;

    return dimension.value === DimensionTypeEnum.ServiceTowers;
  };

  handleApplicationChange = (items: IAllocation[]) => {
    const { selectedItem } = this.state;

    if (!selectedItem) {
      return null;
    }

    const { onAllocationChange, spendType, dimension, selectedYear } =
      this.props;
    const activeAllocation = this.getSelectedAllocation();
    let updatedAllocation;

    if (activeAllocation) {
      updatedAllocation = {
        ...activeAllocation,
        items,
      };
    } else {
      updatedAllocation = {
        contribution: contributionField[spendType],
        type: dimension.value,
        dimensionId: selectedItem.id,
        dimensionName: this.getSelectedDimensionItem()?.name || '',
        rootId: selectedItem.rootId || null,
        level: selectedItem.rootId ? 2 : 1,
        year: selectedYear,
        items,
      };
    }

    onAllocationChange(updatedAllocation);
  };

  handleApplicationRemove = (appId: number) => {
    const { onAllocationChange } = this.props;
    const activeAllocation = this.getSelectedAllocation();

    if (activeAllocation) {
      onAllocationChange({
        ...activeAllocation,
        items: activeAllocation.items.filter((item) => item.id !== appId),
      });
    }
  };

  render() {
    const { selectedItem } = this.state;
    const {
      spendType,
      format,
      applications,
      onDimensionChange,
      onReuseAllocation,
      dimension,
      isDimensionsLoading,
    } = this.props;
    const selectedItemSpend = this.getSelectedDimensionItem()?.spend || 0;
    const selectedItemName = this.getSelectedDimensionItem()?.name;

    return (
      <React.Fragment>
        <MainWrapper>
          <LeftSection>
            <DimensionControls
              onDimensionChange={onDimensionChange}
              onReuseAllocation={onReuseAllocation}
              options={dimensionOptions[spendType]}
              dimension={dimension}
              message={infoMessage[spendType]}
            />
            <OverlayContainer>
              {isDimensionsLoading && <Overlay color={colors.grey1} />}
              <DimensionList
                selection={selectedItem}
                onSelect={this.handleItemSelect}
                data={this.getAllocatedDimensionList()}
                disableGroupSelection={this.isGroupSelectionDisabled()}
                format={format}
              />
            </OverlayContainer>
          </LeftSection>
          <MiddleContainer>
            <ScrollWrapper>
              <MiddleContent
                spend={selectedItemSpend}
                format={format}
                spendType={spendType}
                parentName={this.getSelectedParentName()}
                name={selectedItemName}
                allocations={this.getSelectedAllocation()?.items}
                onAppRemove={this.handleApplicationRemove}
                title="application"
                dimensionId={this.getSelectedAllocation()?.dimensionId}
              />
            </ScrollWrapper>
          </MiddleContainer>
          <SideContainer>
            {!selectedItem && <Overlay />}
            <AllocationList
              items={applications.map(
                ({ appName, corpApp, id, groupName }) => ({
                  name: appName,
                  flag: !!corpApp,
                  id,
                  groupName,
                })
              )}
              allocations={this.getSelectedAllocation()?.items}
              onChange={this.handleApplicationChange}
            />
          </SideContainer>
        </MainWrapper>
      </React.Fragment>
    );
  }
}
